/* src/components/Sidebar.jsx - Modificado el 26-02-2026 */
/* ----------------------------------------------------- */

import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { sidebarMenu } from "../config/sidebarMenu";

import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  useMediaQuery,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const SIDEBAR_WIDTH = 220;
const NAVBAR_HEIGHT = 64;

const MENU_GROUPS = {
  CLIENTES: ["admin", "clientes", "clientes_extractores", "clientes_subir"],
  ENROLADOS: ["admin", "enrolados", "enrolados_extractores", "enrolados_subir"],
  CESIONES: ["admin", "deudor", "cesionario", "cedente", "cesiones_extractores"],
  RECURSOS: ["admin", "recursos"],
  PROVEEDORES: ["admin", "proveedores"],
};

/* 🎞️ Animaciones */
const sidebarVariants = {
  open: {
    width: SIDEBAR_WIDTH,
    opacity: 1,
    transition: {
      width: { duration: 0.45, ease: [0.25, 0.8, 0.25, 1] },
      opacity: { duration: 0.25, delay: 0.1 },
    },
  },
  closed: {
    width: 0,
    opacity: 0,
    transition: {
      width: { duration: 0.38, ease: [0.4, 0.0, 0.2, 1] },
      opacity: { duration: 0.18 },
    },
  },
};

const Sidebar = ({isSidebarOpen, selectedMenu, toggleSidebar = () => {} }) => {
  const { user } = useAuth();
  const location = useLocation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const activeGroup = selectedMenu || "CLIENTES";
  const [openMenu, setOpenMenu] = useState(null);

  /* 🔹 Controla que solo haya un grupo abierto */
  useEffect(() => {
    const groupMenus = MENU_GROUPS[activeGroup] || [];
    const firstNonAdminMenu =
      groupMenus.find((id) => id !== "admin") || null;

    setOpenMenu(firstNonAdminMenu);
  }, [activeGroup]);

  if (!user) return null;

  const visibleMenus = sidebarMenu.filter(
    (m) =>
      (MENU_GROUPS[activeGroup] || []).includes(m.id) &&
      m.roles.includes(user.rol) &&
      (!m.tipos || m.tipos.includes(user.tipo))
  );

  return (
    <>
      {/* Overlay mobile */}
      {isMobile && isSidebarOpen && (
        <Box
          onClick={() => toggleSidebar(false)}
          sx={(theme) => ({
            position: "fixed",
            inset: 0,
            backgroundColor: theme.custom.ui.backdropBg,
            zIndex: 1399,
          })}
        />
      )}

        <motion.aside
          variants={sidebarVariants}
          initial={false}
          animate={isSidebarOpen ? "open" : "closed"}
          style={{
            width: isSidebarOpen ? SIDEBAR_WIDTH : 0,
            flexShrink: 0,
            pointerEvents: isSidebarOpen ? "auto" : "none",
          }}
        >
          <Box
            sx={(theme) => ({
              position: "sticky",
              top: `${NAVBAR_HEIGHT}px`,      // ✅ se pega bajo navbar
              height: `calc(100vh - ${NAVBAR_HEIGHT}px)`, // ✅ altura real visible
              backgroundColor: theme.custom.ui.navBg,
              color: theme.palette.common.white,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            })}
          >
            {/* MENÚ */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
              <Box sx={{ height: "100%", overflowY: "auto", p: 2 }}>
                <List disablePadding>
                  {visibleMenus.map((menu) => {
                    const isOpen = openMenu === menu.id;

                    return (
                      <Box key={menu.id} sx={{ mb: 1 }}>
                        {/* Menú principal */}
                        <ListItemButton
                          onClick={() =>
                            setOpenMenu(isOpen ? null : menu.id)
                          }
                          sx={(theme) => ({
                            px: 2,
                            py: 1,
                            borderRadius: 8,
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            letterSpacing: "0.02em",

                            "&:hover": {
                              backgroundColor:
                                theme.custom.ui.navHoverBg,
                            },
                          })}
                        >
                          <ListItemText primary={menu.title} />
                          {isOpen ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </ListItemButton>

                        {/* Submenú */}
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <List
                            component="div"
                            disablePadding
                            sx={(theme) => ({
                              mt: 0.5,
                              pl: 2,
                              borderLeft: `1px solid ${theme.custom.ui.borderSubtle}`,
                            })}
                          >
                            {menu.items.map((item) => {
                              const isActive = location.pathname.startsWith(item.link);

                              return (
                                <ListItemButton
                                  key={item.link}
                                  component={Link}
                                  to={item.link}
                                  onClick={() => {
                                    if (isMobile && typeof toggleSidebar === "function") {
                                      toggleSidebar(false);
                                    }
                                  }}
                                  sx={(theme) => ({
                                    px: 2,
                                    py: 0.7,
                                    borderRadius: 8,
                                    fontSize: "0.75rem",
                                    fontWeight: isActive ? 500 : 400,

                                    backgroundColor: isActive
                                      ? theme.custom.ui.sidebarItemSelectedBg
                                      : "transparent",

                                    color: isActive
                                      ? theme.custom.ui.sidebarItemTextSelected
                                      : theme.custom.ui.sidebarItemText,

                                    "&:hover": {
                                      backgroundColor: isActive
                                        ? theme.custom.ui.sidebarItemSelectedBg
                                        : theme.custom.ui.sidebarItemHoverBg,
                                    },
                                  })}
                                >
                                  <ListItemText primary={item.name} />
                                </ListItemButton>
                              );
                            })}
                          </List>
                        </Collapse>
                      </Box>
                    );
                  })}
                </List>
              </Box>
            </Box>
          </Box>
        </motion.aside>

    </>
  );
};

export default Sidebar;
